import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../../config/config';

export interface DiaryListItem {
  diaryId: string;
  HomeworkNumber: number;
  diaryTitle: string;
  isAttachmentWithDiary: number;
  staffImage: string;
  staffName: string;
  isHomeworkDone: number;
  subject: string;
  // Not shown in the original API reference's minimal example item shape,
  // but the detail endpoint 422s ("Please provide valid subject id")
  // without one — the real response appears to include it. Optional here
  // since that isn't confirmed yet; openDetail() falls back to the active
  // subject filter if it's absent.
  subjectId?: string;
  dueDate: string;
}

export interface DiaryListResponseBody {
  maxPageNumber: number;
  diaryDetailsForTodaysDueDates?: DiaryListItem[];
  diaryDetailsForUpcomingDueDates?: DiaryListItem[];
  diaryDetailsForPastDueDates?: DiaryListItem[];
  academicStartDate: string;
  academicEndDate: string;
}

export interface DiaryDetailBody {
  diaryData: {
    diaryDetails: { diaryTitle: string; diaryDescription: string; diaryDuration?: number; submissionDate?: string }[];
    diaryMediaDetails: { DiaryMediaType: 'image' | 'document'; DiaryMediaUrl: string; DiaryMediaBlurUrl?: string }[];
  };
}

// Diary — the parent mobile app's own diary endpoints (DiaryList.js /
// DiaryDetailedView.js). List/detail only for now (page 1, no infinite
// scroll; no bounded academic-year date picker for Upcoming/Past — they
// default to diaryDate: "ALL", same as the app's default "All" dropdown
// option) — see the API reference this was built from.
@Injectable({ providedIn: 'root' })
export class ParentDiaryService {

  private base = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) { }

  private authHeaders(): HttpHeaders | undefined {
    const raw = localStorage.getItem('authToken');
    if (!raw) { return undefined; }
    try {
      return new HttpHeaders().append('authToken', JSON.parse(raw));
    } catch {
      return new HttpHeaders().append('authToken', raw);
    }
  }

  getTodaysDiary(studentId: string, classId: string, pageNumber: number, subjectId: string | null, lang: string) {
    return this.http.post(`${this.base}/get/todays/date/diary/for/parent`,
      { studentId, classId, pageNumber, subjectId, lang }, { headers: this.authHeaders() });
  }

  getUpcomingDiary(studentId: string, classId: string, pageNumber: number, subjectId: string | null, lang: string, diaryDate: string = 'ALL') {
    return this.http.post(`${this.base}/get/upcoming/date/diary/for/parent`,
      { studentId, classId, pageNumber, subjectId, lang, diaryDate }, { headers: this.authHeaders() });
  }

  getPastDiary(studentId: string, classId: string, pageNumber: number, subjectId: string | null, lang: string, diaryDate: string = 'ALL') {
    return this.http.post(`${this.base}/get/past/date/diary/for/parent`,
      { studentId, classId, pageNumber, subjectId, lang, diaryDate }, { headers: this.authHeaders() });
  }

  getSubjectFilterList(studentId: string, classId: string, lang: string) {
    return this.http.get(`${this.base}/get/subject/list/for/diary/filter/for/parent`,
      { params: { studentId, classId, lang }, headers: this.authHeaders() });
  }

  markHomeworkDone(studentId: string, classId: string, diaryId: string, lang: string) {
    return this.http.post(`${this.base}/mark/home/work/done`,
      { lang, studentId, classId, diaryId }, { headers: this.authHeaders() });
  }

  getDiaryDetail(studentId: string, classId: string, diaryId: string, subjectId: string | null, lang: string) {
    const params: any = { studentId, classId, diaryId, lang };
    if (subjectId) { params.subjectId = subjectId; }
    return this.http.get(`${this.base}/get/diary/detail/by/diary/id/for/parent`, { params, headers: this.authHeaders() });
  }
}
